import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { apiHosts } from "@/api/constants/apiHosts";
import type { CanDeleteFacility, Facility, FacilityNew } from "@/views/facility/facility";
import type { AxiosResponse } from "axios";
import facilityService from "./facilityService";
import { LE } from "@/constants/enums";
import type { RequestValidationError } from "@/helpers/requestValidationError";

const URL = "facilities";
const ENTITY_NAME = "Facility";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_UST_API_BASE_URL,
};

const api = apiService<Facility, Paging<Facility>>(apiHosts.DWS, axiosParams);
const canDeleteApi = apiService<RequestValidationError>(apiHosts.DWS, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} config.abortMethod Callback for when a request has been aborted
 */
const getFacilities = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getFacilityById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewFacility = async () => {
  //optionally make any other changes as necessary before returning.
  return { data: facilityService.getInitialData() } as AxiosResponse<FacilityNew, any>;
};

const createFacility = (facility: Facility, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, facility, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateFacility = (facility: Facility, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + facility.id, facility, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Facility} facility This cannot be a 'ref' to a facility
 */
const saveFacility = (facility: Facility, config: PagingApiAndNotifierConfig = {}) => {
  if (facility.id) {
    return updateFacility(facility, config);
  } else {
    return createFacility(facility, config);
  }
};

const deleteFacilityById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const canDeleteFacility = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return canDeleteApi.delete(URL + "/" + id + "?" + "validateOnly=" + true, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  getFacilities,
  getFacilityById,
  getNewFacility,
  createFacility,
  updateFacility,
  saveFacility,
  deleteFacilityById,
  canDeleteFacility,
};
